import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { motion } from "framer-motion";
import { Star, MapPin, Trophy, Play, FileText, Activity, ShieldCheck } from "lucide-react";
import { useCms } from "@/hooks/useCms";
import { FlagIcon } from "@/components/FlagIcon";
import { VideoModal } from "@/components/VideoModal";
import { AthleteDossierModal, type DossierAthlete } from "@/components/AthleteDossierModal";
import { AthleteRadarChart } from "@/components/AthleteRadarChart";
import { db } from "@/lib/firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";

type Athlete = {
  id: string;
  name: string;
  sport: string;
  nationality: string;
  position: string;
  age: number | null;
  bio: string;
  image_url: string;
  achievements: string[];
  is_featured: boolean;
  video_url?: string;
};

const SAMPLE_ROSTER: Athlete[] = [
  {
    id: "101",
    name: "Kaelen Voss",
    sport: "Football",
    position: "Central Midfielder",
    nationality: "Germany",
    age: 21,
    bio: "Technically gifted deep-lying playmaker with high-level tactical vision, progressive passing metrics (89%), and Bundesliga Academy development background.",
    image_url: "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800&auto=format&fit=crop",
    achievements: ["U19 Bundesliga Champion", "Top Assist Leader 2025", "Invited to DFB U21 Camp"],
    is_featured: true,
    video_url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  },
  {
    id: "102",
    name: "Emeka Okafor",
    sport: "Football",
    position: "Right Winger / Striker",
    nationality: "Nigeria",
    age: 19,
    bio: "Explosive wide forward with elite top-end speed (34.8 km/h), dynamic 1v1 dribbling mastery, and acute goal instinct in transition phases.",
    image_url: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800&auto=format&fit=crop",
    achievements: ["Golden Boot (West Africa Youth)", "22 Goals in 2025 Season", "Trialist in European Top Flights"],
    is_featured: true,
    video_url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  },
  {
    id: "103",
    name: "Marcus Vance",
    sport: "Football",
    position: "Center Back",
    nationality: "United States",
    age: 23,
    bio: "Dominant 6'3\" modern center back with composed aerial authority, left-footed passing range, and high recovery interception ratings in MLS Next Pro.",
    image_url: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=800&auto=format&fit=crop",
    achievements: ["MLS Next Pro Best XI", "Defensive Player of the Year", "87% Aerial Duel Success"],
    is_featured: true,
    video_url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  },
];

function AthleteCard({
  athlete,
  index,
  onOpenVideo,
  onOpenDossier,
}: {
  athlete: Athlete;
  index: number;
  onOpenVideo: (athlete: Athlete) => void;
  onOpenDossier: (athlete: Athlete) => void;
}) {
  const [showRadar, setShowRadar] = useState(false);

  const getCountryCode = (nat: string) => {
    const l = nat.toLowerCase();
    if (l.includes("germany")) return "DE";
    if (l.includes("nigeria")) return "NG";
    if (l.includes("united states") || l.includes("usa")) return "US";
    if (l.includes("canada")) return "CA";
    if (l.includes("uk") || l.includes("england")) return "GB";
    return "DE";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-slate-950/90 border border-slate-800 rounded-3xl overflow-hidden hover:border-yellow-500/50 transition-all duration-300 group flex flex-col justify-between shadow-xl"
    >
      <div>
        {/* Card Media Header */}
        <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-900">
          {athlete.image_url ? (
            <img
              src={athlete.image_url}
              alt={athlete.name}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-yellow-500/10 flex items-center justify-center">
              <span className="text-yellow-400 font-black text-6xl">{athlete.name.charAt(0)}</span>
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />

          {/* Featured Badge */}
          {athlete.is_featured && (
            <div className="absolute top-3 left-3 bg-yellow-500 text-black text-[11px] font-extrabold px-3 py-1 rounded-full flex items-center gap-1 shadow-lg">
              <Star size={12} className="fill-black" /> Pro Roster
            </div>
          )}

          {/* Flag Badge */}
          <div className="absolute top-3 right-3 bg-slate-950/80 backdrop-blur-md px-2.5 py-1 rounded-full border border-slate-700 flex items-center gap-1.5 shadow-lg">
            <FlagIcon country={getCountryCode(athlete.nationality)} size="sm" />
            <span className="text-[11px] font-bold text-white uppercase tracking-wider">
              {getCountryCode(athlete.nationality)}
            </span>
          </div>

          {/* Quick Action Overlay Buttons */}
          <div className="absolute bottom-3 right-3 flex items-center gap-2">
            {athlete.video_url && (
              <button
                onClick={() => onOpenVideo(athlete)}
                className="p-2.5 rounded-xl bg-yellow-500 hover:bg-yellow-400 text-black font-bold shadow-lg transition-transform hover:scale-105 active:scale-95"
                title="Play Match Highlights"
              >
                <Play className="w-4 h-4 fill-black" />
              </button>
            )}
            <button
              onClick={() => onOpenDossier(athlete)}
              className="p-2.5 rounded-xl bg-slate-900/90 hover:bg-slate-800 text-yellow-400 border border-slate-700 shadow-lg transition-transform hover:scale-105 active:scale-95"
              title="View Scouting Dossier"
            >
              <FileText className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Card Content */}
        <div className="p-6">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-xl font-black text-white group-hover:text-yellow-400 transition-colors">
                {athlete.name}
              </h3>
              <p className="text-yellow-400/90 text-xs font-bold uppercase tracking-wider mt-0.5">
                {athlete.position} • {athlete.sport}
              </p>
            </div>
            {athlete.age && (
              <span className="px-2.5 py-0.5 rounded-full bg-slate-900 border border-slate-800 text-slate-400 text-xs font-bold">
                {athlete.age} yrs
              </span>
            )}
          </div>

          {/* Radar Attributes Toggle */}
          {showRadar ? (
            <div className="mt-4 p-3 bg-slate-900/80 rounded-2xl border border-slate-800 flex flex-col items-center">
              <AthleteRadarChart size={200} />
              <button
                onClick={() => setShowRadar(false)}
                className="text-[11px] text-slate-400 hover:text-white mt-1 underline"
              >
                Hide Performance Radar
              </button>
            </div>
          ) : (
            <>
              {athlete.bio && (
                <p className="text-xs sm:text-sm text-slate-400 mt-3 leading-relaxed line-clamp-3">
                  {athlete.bio}
                </p>
              )}

              {/* Achievements tags */}
              {athlete.achievements && athlete.achievements.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {athlete.achievements.slice(0, 3).map((ach, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 text-[11px] bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 font-medium px-2.5 py-0.5 rounded-full"
                    >
                      <Trophy size={10} /> {ach}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Card Action Footer */}
      <div className="p-6 pt-0 flex items-center gap-2">
        <button
          onClick={() => onOpenDossier(athlete)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-800 text-white text-xs font-bold transition-colors"
        >
          <FileText className="w-3.5 h-3.5 text-yellow-400" /> View Profile
        </button>
        <button
          onClick={() => setShowRadar(!showRadar)}
          className={`p-2.5 rounded-xl border text-xs font-bold transition-colors ${
            showRadar
              ? "bg-yellow-500 text-black border-yellow-500"
              : "bg-slate-900 hover:bg-slate-800 border-slate-800 text-slate-300"
          }`}
          title="Toggle Attribute Radar"
        >
          <Activity className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

export default function Athletes() {
  const [positionFilter, setPositionFilter] = useState("All");
  const [activeVideo, setActiveVideo] = useState<Athlete | null>(null);
  const [activeDossier, setActiveDossier] = useState<DossierAthlete | null>(null);

  const { data: dbAthletes = [], isLoading } = useQuery<Athlete[]>({
    queryKey: ["/api/athletes"],
    queryFn: async () => {
      try {
        const q = query(collection(db, "athletes"), orderBy("display_order", "asc"));
        const snap = await getDocs(q);
        return snap.docs.map(d => {
          const item = d.data();
          let parsedAchievements: string[] = [];
          if (Array.isArray(item.achievements)) {
            parsedAchievements = item.achievements;
          } else if (typeof item.achievements === "string") {
            try {
              parsedAchievements = JSON.parse(item.achievements);
            } catch {
              parsedAchievements = [];
            }
          }
          return {
            id: d.id,
            name: item.name || "",
            sport: item.sport || "",
            nationality: item.nationality || "",
            position: item.position || "",
            age: item.age || null,
            bio: item.bio || "",
            image_url: item.image_url || "",
            achievements: parsedAchievements,
            is_featured: item.is_featured || false,
            video_url: item.video_url || "",
          };
        });
      } catch (err) {
        console.error("Failed to fetch athletes from Firestore:", err);
        return [];
      }
    },
  });

  const athletes = dbAthletes.length > 0 ? dbAthletes : SAMPLE_ROSTER;

  const { getMeta } = useCms();
  const heroMeta = getMeta<{ heading: string; subtext: string }>("athletes_hero", {
    heading: "OUR REPRESENTED ATHLETES",
    subtext:
      "Explore our international talent roster. FIFA-verified dossiers, technical radar metrics, and match footage for clubs and scouts.",
  });

  const positions = ["All", "Forward", "Midfielder", "Defender", "Goalkeeper"];

  const filtered =
    positionFilter === "All"
      ? athletes
      : athletes.filter((a) =>
          (a.position || "").toLowerCase().includes(positionFilter.toLowerCase())
        );

  const handleOpenDossier = (a: Athlete) => {
    setActiveDossier({
      id: a.id,
      name: a.name,
      sport: a.sport,
      position: a.position,
      nationality: a.nationality,
      age: a.age,
      bio: a.bio,
      imageUrl: a.image_url,
      videoUrl: a.video_url,
      achievements: a.achievements,
      attributes: {
        pace: 88,
        technical: 85,
        tactical: 82,
        physical: 86,
        mental: 84,
        finishing: 80,
      },
    });
  };

  return (
    <>
      <SeoHead
        title="Represented Athletes & Scouting Roster"
        description="Explore the represented elite athletes and scouting talent roster of Eliada Sports Agency across European, North American, and Global leagues."
        path="/athletes"
      />

      <PageTransition className="pt-24 pb-24 bg-slate-950 text-white min-h-screen">
        {/* Hero Section */}
        <div className="relative py-20 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950 border-b border-slate-800">
          <div className="absolute inset-0 bg-yellow-500/5 blur-3xl pointer-events-none" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs font-bold uppercase tracking-widest mb-4">
              <ShieldCheck className="w-3.5 h-3.5" /> Transfermarkt & FIFA Scouting Grade
            </div>
            <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight mb-4">
              Elite Athlete <span className="text-yellow-400">Roster</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              {heroMeta.subtext}
            </p>
          </div>
        </div>

        {/* Filters & Roster Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          {/* Position Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {positions.map((pos) => (
              <button
                key={pos}
                onClick={() => setPositionFilter(pos)}
                className={`px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                  positionFilter === pos
                    ? "bg-yellow-500 text-black shadow-lg shadow-yellow-500/20 scale-105"
                    : "bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800"
                }`}
              >
                {pos}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-24">
              <div className="animate-spin w-10 h-10 border-2 border-yellow-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((a, i) => (
                <AthleteCard
                  key={a.id}
                  athlete={a}
                  index={i}
                  onOpenVideo={(ath) => setActiveVideo(ath)}
                  onOpenDossier={handleOpenDossier}
                />
              ))}
            </div>
          )}
        </div>
      </PageTransition>

      {/* Video Modal */}
      <VideoModal
        isOpen={!!activeVideo}
        onClose={() => setActiveVideo(null)}
        title={activeVideo?.name ? `${activeVideo.name} — Match Scouting Reel` : "Match Highlights"}
        videoUrl={activeVideo?.video_url || "https://www.youtube.com/watch?v=dQw4w9WgXcQ"}
        athleteName={activeVideo?.name}
      />

      {/* Dossier Modal */}
      <AthleteDossierModal
        isOpen={!!activeDossier}
        onClose={() => setActiveDossier(null)}
        athlete={activeDossier}
      />
    </>
  );
}
